import React, { useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import axios from 'axios';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Lists from './pages/Lists';
import Subscribers from './pages/Subscribers';
import Campaigns from './pages/Campaigns';
import CreateCampaign from './pages/CreateCampaign';
import Reports from './pages/Reports';
import Suppression from './pages/Suppression';
import Templates from './pages/Templates';
import Settings from './pages/Settings';
import Unsubscribe from './pages/Unsubscribe';
import ResetPassword from './pages/ResetPassword';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/AppLayout';

const PUBLIC_PATHS = ['/login', '/unsubscribe', '/reset-password'];

axios.defaults.withCredentials = true;

const AxiosInterceptor = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          localStorage.removeItem('isLoggedIn');
          navigate('/login', { replace: true });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);
  return null;
};

const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.some(p => location.pathname.startsWith(p));
  const isAuthenticated = !!localStorage.getItem('isLoggedIn');
  if (isPublic || !isAuthenticated) return <>{children}</>;
  return <AppLayout>{children}</AppLayout>;
};

const ThemedApp = () => {
  const { theme } = useContext(ThemeContext);

  const algorithm = theme.mode === 'dark'
    ? [antdTheme.darkAlgorithm]
    : [antdTheme.defaultAlgorithm];

  const antConfig = {
    algorithm,
    token: {
      colorPrimary:  theme.colorPrimary,
      colorSuccess:  '#9DC533',
      colorWarning:  '#f59e0b',
      colorError:    '#ef4444',
      borderRadius:  theme.borderRadius,
      fontFamily:    "'Inter', -apple-system, sans-serif",
    },
  };

  return (
    <ConfigProvider theme={antConfig}>
      <Router>
        <AxiosInterceptor />
        <LayoutWrapper>
          <Routes>
            <Route path="/login"          element={<Login />} />
            <Route path="/unsubscribe"    element={<Unsubscribe />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/"                     element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/lists"                element={<PrivateRoute><Lists /></PrivateRoute>} />
            <Route path="/subscribers"          element={<PrivateRoute><Subscribers /></PrivateRoute>} />
            <Route path="/campaigns"            element={<PrivateRoute><Campaigns /></PrivateRoute>} />
            <Route path="/campaigns/create"     element={<PrivateRoute><CreateCampaign /></PrivateRoute>} />
            <Route path="/campaigns/:id/edit"   element={<PrivateRoute><CreateCampaign /></PrivateRoute>} />
            <Route path="/campaigns/:id/report" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/templates"            element={<PrivateRoute><Templates /></PrivateRoute>} />
            <Route path="/suppression"          element={<PrivateRoute><Suppression /></PrivateRoute>} />
            <Route path="/settings"             element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="*"                     element={<Navigate to="/" />} />
          </Routes>
        </LayoutWrapper>
      </Router>
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
